// Order Status Modal Component
// Allows users to check their order status by confirmation ID, last name, and zip code

import { useState, useRef, useEffect } from 'react';
import { getOrderStatus } from '@/services/checkoutService';
import { logger } from '@/utils/logger';
import type { OrderDetails, OrderPlan } from '@/types';
import '@/styles/components/order-status.css';

interface OrderStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OrderStatusModal({ isOpen, onClose }: OrderStatusModalProps) {
  const [confirmationId, setConfirmationId] = useState('');
  const [lastName, setLastName] = useState('');
  const [serviceZip, setServiceZip] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderNotFound, setOrderNotFound] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);

  const confirmationInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && confirmationInputRef.current) {
      confirmationInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationId('');
      setLastName('');
      setServiceZip('');
      setError(null);
      setOrderNotFound(false);
      setOrderDetails(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    // Validate inputs
    if (!confirmationId.trim() || !lastName.trim() || !serviceZip.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    // Validate zip code format
    if (!/^\d{5}$/.test(serviceZip.trim())) {
      setError('Please enter a valid 5-digit zip code.');
      return;
    }

    setError(null);
    setOrderNotFound(false);
    setOrderDetails(null);
    setIsLoading(true);

    try {
      const result = await getOrderStatus({
        confirmationId: confirmationId.trim(),
        lastName: lastName.trim(),
        serviceZip: serviceZip.trim(),
      });

      if (result.success && result.data) {
        if (result.data.orderExists && result.data.order) {
          setOrderDetails(result.data.order);
        } else {
          setOrderNotFound(true);
        }
      } else {
        setError(result.error || 'Unable to retrieve order status. Please try again.');
      }

      // Scroll to results
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      logger.error({ area: 'order-status' }, 'Order status error', err instanceof Error ? err : undefined);
      setError('Unable to retrieve order status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal__container">
        <div className="modal__header">
          <h2 className="modal__title">Check Order Status</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal__body">
          <p style={{ marginBottom: '24px', color: '#636970' }}>
            Enter your order details below to check the status of your services.
          </p>

          <div className="form-group">
            <label htmlFor="confirmation-id" className="form-label">Confirmation ID</label>
            <input
              ref={confirmationInputRef}
              type="text"
              id="confirmation-id"
              value={confirmationId}
              onChange={(e) => setConfirmationId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., ABC123"
            />
          </div>

          <div className="form-group">
            <label htmlFor="order-last-name" className="form-label">Last Name</label>
            <input
              type="text"
              id="order-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter your last name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="order-zip" className="form-label">Service Zip Code</label>
            <input
              type="text"
              id="order-zip"
              value={serviceZip}
              onChange={(e) => setServiceZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              onKeyPress={handleKeyPress}
              placeholder="5-digit zip code"
              maxLength={5}
            />
          </div>

          <div className="action-row" style={{ marginTop: '24px' }}>
            <button
              type="button"
              className="btn btn--full-width"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'CHECKING...' : 'CHECK STATUS'}
            </button>
          </div>

          {/* Results Section */}
          <div ref={resultsRef} className="order-status-results">
            {error && (
              <div className="order-status-card order-status-card--error">
                <p>{error}</p>
              </div>
            )}

            {orderNotFound && (
              <div className="order-status-card order-status-card--not-found">
                <h3>Order Not Found</h3>
                <p>We couldn't find an order matching the information provided. Please check your details and try again.</p>
              </div>
            )}

            {orderDetails && <OrderDetailsCard order={orderDetails} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sub-component for rendering order details
function OrderDetailsCard({ order }: { order: OrderDetails }) {
  const orderDate = new Date(order.OrderDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <div className="order-status-card">
        <div className="order-status-header">
          <h3>Order Found</h3>
          <span className="order-confirmation">Confirmation: {order.ConfirmationId}</span>
        </div>
        <div className="order-status-info">
          <p><strong>Name:</strong> {order.FirstName} {order.LastName}</p>
          <p><strong>Service Address:</strong> {order.ServiceAddress}, {order.ServiceCity}, {order.ServiceState}</p>
          <p><strong>Order Date:</strong> {orderDate}</p>
        </div>
      </div>

      {order.OrderPlans && order.OrderPlans.length > 0 && (
        <div className="order-status-plans">
          <h4>Services</h4>
          {order.OrderPlans.map((plan, index) => (
            <OrderPlanCard key={index} plan={plan} />
          ))}
        </div>
      )}
    </>
  );
}

// Sub-component for rendering a single service plan
function OrderPlanCard({ plan }: { plan: OrderPlan }) {
  const startDate = new Date(plan.ServiceStartDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const getStatusClass = (statusName: string): string => {
    const statusLower = (statusName || '').toLowerCase();
    if (statusLower === 'new' || statusLower === 'pending') {
      return 'status-badge--new';
    } else if (statusLower === 'in progress' || statusLower === 'processing') {
      return 'status-badge--pending';
    } else if (statusLower === 'complete' || statusLower === 'completed' || statusLower === 'active') {
      return 'status-badge--complete';
    } else if (statusLower === 'cancelled' || statusLower === 'failed') {
      return 'status-badge--cancelled';
    }
    return 'status-badge--new';
  };

  return (
    <div className="order-plan-card">
      <div className="order-plan-header">
        {plan.VendorLogo ? (
          <img src={plan.VendorLogo} alt={plan.VendorName} className="order-plan-logo" />
        ) : (
          <div className="order-plan-logo-placeholder" />
        )}
        <div className="order-plan-info">
          <span className="order-plan-service">{plan.ServiceName}</span>
          <span className="order-plan-name">{plan.PlanName}</span>
          <span className="order-plan-vendor">{plan.VendorName}</span>
        </div>
      </div>
      <div className="order-plan-status">
        <span className={`status-badge ${getStatusClass(plan.StatusName)}`}>{plan.StatusName}</span>
        <span className="order-plan-date">Start: {startDate}</span>
      </div>
    </div>
  );
}
